import * as React from 'react';
import {
  ResponsiveBarCanvas,
  BarCanvasProps,
  BarDatum,
  BarTooltipProps,
} from '@nivo/bar';
import classNames from 'classnames';
import deepmerge from 'deepmerge';
import toArray from 'lodash/toArray';
import difference from 'lodash/difference';
import { useScrollContainer } from 'react-indiana-drag-scroll';
import { useTheme } from '../../../contexts/theme';
import { ColorStyleProps } from '../../../@types/theme';
import { parseChartColors } from '../../../utils/themeUtils';
import ChartTooltip from '../../atoms/ChartTooltip';
import Spinner from '../../atoms/Spinner';
import Badge from '../../atoms/Badge';
import Legend from '../Legend';

import styles from './BarChart.module.scss';

// Interfaces
// //////////////////////////

export type ChartOptions = Partial<BarCanvasProps<BarDatum>>;

export type ChartDatum = BarDatum;

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: Array<BarDatum>;
  keys: Array<string>;
  indexBy: string;
  options?: ChartOptions;
  height?: number;
  tooltipShowPercentage?: boolean;
  allowDecimalScale?: boolean;
  allowScroll?: boolean;
  displayLegend?: boolean;
  displayAverage?: boolean;
  loading?: boolean;
}

/**
 * BarChart Tooltip
 */
const Tooltip: React.FC<BarTooltipProps<BarDatum>> = (
  { label, color, formattedValue, ...rest },
  tooltipShowPercentage,
) => {
  let value: number;
  let newFormattedValue: string;

  const hasFormattedValue = rest?.data[rest.id]?.toString() !== formattedValue;

  if (tooltipShowPercentage) {
    value = Math.round((Number(rest.data[`${rest.id}%`]) || 0) * 100);
    newFormattedValue = `${
      hasFormattedValue ? formattedValue : Number(rest.data[rest.id])
    } (${value}%)`;
  } else {
    value = Number(rest.value) || 0;
    newFormattedValue = hasFormattedValue
      ? formattedValue
      : (Number(rest.value) || 0).toString();
  }

  return (
    <ChartTooltip
      datum={{
        id: rest.id as string,
        color,
        label,
        value,
        formattedValue: newFormattedValue,
        data: { id: rest.id as string },
        hidden: false,
      }}
      useFormattedValue
    />
  );
};

/**
 * Default chart options
 */
const defaultOptions: ChartOptions = {
  margin: {
    top: 10,
    right: 20,
    bottom: 50,
    left: 60,
  },
  padding: 0.3,
  groupMode: 'grouped',
  colors: { scheme: 'paired' },
  borderColor: 'inherit:darker(1.6)',
  enableLabel: false,
  enableGridX: false,
  axisLeft: {
    ticksPosition: 'before',
    tickSize: 0,
    tickPadding: 5,
    tickRotation: 0,
    legendPosition: 'middle',
    legendOffset: -40,
  },
  axisBottom: {
    ticksPosition: 'after',
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0,
    legendPosition: 'middle',
    legendOffset: 32,
  },
  labelOffset: 4,
  labelSkipWidth: 12,
  labelSkipHeight: 12,
  labelTextColor: {
    from: 'color',
    modifiers: [['darker', 2]],
  },
  label: (d) => {
    const percentage = Math.round(Number(d.data[`${d.id}%`]) * 100);
    return `${d.value} (${percentage}%)`;
  },
  legends: [],
  theme: {
    grid: {
      line: {
        strokeWidth: 0.5,
      },
    },
  },
};

/**
 * <BarChart> component
 */
export const BarChart: React.FunctionComponent<Props> = ({
  className,
  data,
  keys,
  indexBy,
  options = {},
  height = 300,
  tooltipShowPercentage = false,
  allowDecimalScale = true,
  allowScroll = false,
  displayLegend = false,
  displayAverage = false,
  loading,
  ...htmlElementProps
}: Props) => {
  const { theme } = useTheme();
  const scrollContainer = useScrollContainer();

  const colors = React.useMemo(
    () =>
      (options.colors
        ? toArray(options.colors)
        : theme.color.chart) as ColorStyleProps[],
    [options.colors, theme],
  );

  const [visibleKeys, setVisibleKeys] = React.useState(keys);
  const [colorIndexBlacklist, setColorIndexBlacklist] = React.useState<
    number[]
  >([]);

  /** Force visibleKeys initial value to be keys */
  React.useEffect(() => setVisibleKeys(keys), [keys]);

  /**
   * Toggles bars when a legend's badge is clicked
   */
  const toggleData = React.useCallback(
    (key: string | number, index: string | number) => {
      const removedKeys = difference(keys, visibleKeys);

      if (visibleKeys.includes(key as string)) {
        // deselect label
        setVisibleKeys(visibleKeys.filter((k) => k !== key));
        setColorIndexBlacklist([...colorIndexBlacklist, index as number]);
      } else {
        // re-select label
        setVisibleKeys(
          keys.filter((k) => !removedKeys.includes(k) || k === key),
        );
        setColorIndexBlacklist(
          colorIndexBlacklist.filter((v) => v !== (index as number)),
        );
      }
    },
    [colorIndexBlacklist, keys, visibleKeys],
  );

  const visibleColors = React.useMemo(
    () =>
      colors.filter((_color, _index) => !colorIndexBlacklist.includes(_index)),
    [colorIndexBlacklist, colors],
  );

  /* ---------------------------------------
   * Merge default, user and dynamic options
   --------------------------------------- */

  const dynamicOptions: Partial<BarCanvasProps<BarDatum>> = React.useMemo(
    () => ({
      colors,
      theme: {
        fontSize: 12,
        fontFamily: theme.typography.fontFamily,
        textColor: theme.color.gray200,
        grid: {
          line: {
            stroke: theme.color.gray100,
          },
        },
      },
      axisLeft: {
        format: !allowDecimalScale
          ? (e) => (Math.floor(e) === e ? e : '')
          : (e) => e,
        legend: options.axisLeft?.legend ? options.axisLeft.legend : '',
      },
      tooltip: (e) => Tooltip(e, tooltipShowPercentage),
    }),
    [theme, allowDecimalScale, colors, options.axisLeft, tooltipShowPercentage],
  );

  const opts: ChartOptions = React.useMemo(() => {
    const res = deepmerge(defaultOptions, options);
    // Dynamic options will overlay user options if needed
    const withDynamicOptions = deepmerge(res, dynamicOptions);

    if (options.valueFormat) {
      withDynamicOptions.valueFormat = options.valueFormat;
    }

    if (options.axisLeft?.format) {
      withDynamicOptions.axisLeft = {
        ...withDynamicOptions.axisLeft,
        format: options.axisLeft.format,
      };
    }

    if (options.axisBottom?.format) {
      withDynamicOptions.axisBottom = {
        ...withDynamicOptions.axisBottom,
        format: options.axisBottom.format,
      };
    }

    if (options.axisRight?.format) {
      withDynamicOptions.axisRight = {
        ...withDynamicOptions.axisRight,
        format: options.axisRight.format,
      };
    }

    if (options.axisTop?.format) {
      withDynamicOptions.axisTop = {
        ...withDynamicOptions.axisTop,
        format: options.axisTop.format,
      };
    }

    return withDynamicOptions;
  }, [dynamicOptions, options]);

  // Calculate chart average:
  //
  const average: number = React.useMemo(() => {
    let sum = 0;
    const answers = data.length;

    data.forEach((row) => {
      visibleKeys.forEach((key) => {
        // TODO: Check this logic, the following was not working for
        // "[ { date: 'somedate1', category1: 50, category2: 150 } ]":
        // sum += Number(key) * Number(row[key]);
        // answers += Number(row[key]);
        sum += Number(row[key]);
      });
    });

    return Number((sum / answers).toPrecision(2)) || 0;
  }, [data, visibleKeys]);

  const wrapperCls = classNames(styles.wrapper, className, {
    [styles.loading]: loading,
  });

  return (
    <div className={wrapperCls} {...htmlElementProps}>
      {loading && <Spinner data-testid="barchart-spinner" />}

      {!loading && (
        <>
          {displayAverage && (
            <div className={styles['avg-container']}>
              <Badge>
                Média: &nbsp;<strong>{average}</strong>
              </Badge>
            </div>
          )}
          <div
            ref={scrollContainer.ref}
            className={classNames({
              'overflow-x-scroll overflow-y-hidden': allowScroll,
            })}
          >
            <div
              style={{
                height,
                minWidth: allowScroll ? data.length * 200 : '100%',
              }}
              data-testid="chart-wrapper"
            >
              <ResponsiveBarCanvas
                data={data}
                keys={visibleKeys}
                indexBy={indexBy}
                {...opts}
                colors={parseChartColors(visibleColors)}
              />
            </div>
          </div>

          {displayLegend && (
            <Legend
              keys={keys.map((k) => ({
                label: k,
                disabled: data.every((d) => d[k] === 0),
              }))}
              colors={colors}
              onClick={toggleData}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BarChart;
